if (!customElements.get('cart-coupon')) {
  customElements.define(
    'cart-coupon',
    class CartCoupon extends HTMLElement {
      constructor() {
        super();
        
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount_code"]');
        this.applyButton = this.querySelector('.coupon-apply-btn');
        this.errorMessage = this.querySelector('.coupon-error');
        this.successMessage = this.querySelector('.coupon-success');
        this.appliedCoupons = this.querySelector('.applied-coupons');
        
        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
        
        // Listen for coupon removal
        this.addEventListener('click', this.handleRemoveCoupon.bind(this));
        
        // Check for existing discount codes on load
        this.checkExistingDiscounts();
        
        // Check if we just applied a coupon
        this.checkCouponApplication();
      }
      
      handleSubmit(event) {
        event.preventDefault();
        
        const discountCode = this.input.value.trim();
        // If no discount code, hide loading, show error, and return
        if (!discountCode) {
          this.hideLoading(); // Reset button state immediately
          this.showError(); 
          return;
        }
        
        // Only show loading and clear messages if there IS a discount code
        this.showLoading(); 
        this.clearMessages(); 
        
        this.applyCoupon(discountCode);
      }
      
      async applyCoupon(discountCode) {
        try {
          // Apply discount by navigating to discount URL and then back
          const currentUrl = window.location.href;
          const discountUrl = `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(currentUrl)}`;
          
          // Store that we're applying a coupon to show success message when returning
          sessionStorage.setItem('applying_coupon', discountCode);
          
          // Store cart drawer state to keep it open after redirect
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            sessionStorage.setItem('cart_drawer_open', 'true');
          }
          
          // Navigate to discount URL
          window.location.href = discountUrl;
          
        } catch (error) {
          console.error('Error applying coupon:', error);
          this.showError();
          this.hideLoading();
        }
      }
      
      handleRemoveCoupon(event) {
        if (event.target.closest('.coupon-remove')) {
          event.preventDefault();
          const couponCode = event.target.closest('.coupon-tag').dataset.couponCode;
          this.removeCoupon(couponCode);
        }
      }
      
      async removeCoupon(couponCode) {
        try {
          // Navigate to clear discount URL
          const currentUrl = window.location.href;
          const clearUrl = `/discount/CLEAR?redirect=${encodeURIComponent(currentUrl)}`;
          
          // Store cart drawer state to keep it open after redirect
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            sessionStorage.setItem('cart_drawer_open', 'true');
          }
          
          window.location.href = clearUrl;
          
        } catch (error) {
          console.error('Error removing coupon:', error);
        }
      }
      
      async refreshCartDrawer() {
        // Trigger cart refresh event
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { open: true }
        }));
      }
      
      checkExistingDiscounts() {
        // Check if there are existing discounts in the cart
        const discountElements = document.querySelectorAll('.discounts__discount');
        if (discountElements.length > 0) {
          this.updateAppliedCoupons(discountElements);
        }
        
        // Also check for pre-rendered coupon tags in the applied-coupons div
        if (this.appliedCoupons) {
          const existingTags = this.appliedCoupons.querySelectorAll('.coupon-tag');
          if (existingTags.length > 0) {
            this.appliedCoupons.style.display = 'block';
          }
        }
      }
      
      checkCouponApplication() {
        // Check if we just applied a coupon
        const appliedCoupon = sessionStorage.getItem('applying_coupon');
        const shouldOpenDrawer = sessionStorage.getItem('cart_drawer_open');
        
        if (appliedCoupon) {
          sessionStorage.removeItem('applying_coupon');
          
          // Check if the coupon was actually applied
          const discountElements = document.querySelectorAll('.discounts__discount');
          let couponFound = false;
          
          discountElements.forEach(discount => {
            const discountTitle = discount.textContent.trim().split('-')[0].trim();
            if (discountTitle.toLowerCase().includes(appliedCoupon.toLowerCase())) {
              couponFound = true;
            }
          });
          
          if (couponFound) {
            this.showSuccess();
          } else {
            this.showError();
          }
        }
        
        // Restore cart drawer state if it was open before coupon application
        if (shouldOpenDrawer === 'true') {
          sessionStorage.removeItem('cart_drawer_open');
          
          // Use a small delay to ensure the page is fully loaded
          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.show) {
              cartDrawer.show();
            }
          }, 100);
        }
      }
      
      updateAppliedCoupons(discountElements) {
        if (!this.appliedCoupons) return;
        
        // Clear existing content
        this.appliedCoupons.innerHTML = '';
        
        // Track unique coupon codes to avoid duplicates
        const uniqueCoupons = new Set();
        
        discountElements.forEach(discount => {
          const discountTitle = discount.textContent.trim().split('-')[0].trim();
          
          // Only add if we haven't seen this coupon code before
          if (!uniqueCoupons.has(discountTitle)) {
            uniqueCoupons.add(discountTitle);
            const couponTag = this.createCouponTag(discountTitle);
            this.appliedCoupons.appendChild(couponTag);
          }
        });
        
        // The CSS will handle hiding when empty
      }
      
      createCouponTag(couponCode) {
        const tag = document.createElement('div');
        tag.className = 'coupon-tag';
        tag.dataset.couponCode = couponCode;
        
        tag.innerHTML = `
          <span class="coupon-code">${couponCode}</span>
          <button type="button" class="coupon-remove" aria-label="Remove ${couponCode}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
        
        return tag;
      }
      
      showLoading() {
        if (this.applyButton) {
          this.applyButton.disabled = true;
          // Manage aria-busy for the HoverButton theme component
          if (this.applyButton.hasAttribute('is') && this.applyButton.getAttribute('is') === 'hover-button') {
            this.applyButton.setAttribute('aria-busy', 'true');
          }
          const btnText = this.applyButton.querySelector('.btn-text');
          if (btnText) {
            btnText.textContent = 'Applying...';
          } else {
            this.applyButton.textContent = 'Applying...';
          }
        }
      }
      
      hideLoading() {
        if (this.applyButton) {
          this.applyButton.disabled = false;
          // Manage aria-busy for the HoverButton theme component
          if (this.applyButton.hasAttribute('is') && this.applyButton.getAttribute('is') === 'hover-button') {
            this.applyButton.removeAttribute('aria-busy'); 
          }
          
          const btnText = this.applyButton.querySelector('.btn-text');
          const originalText = this.applyButton.dataset.originalText || 'Apply';
          if (btnText) {
            btnText.textContent = originalText;
          } else {
            this.applyButton.textContent = originalText;
          }
        }
      }
      
      showError() {
        if (this.errorMessage) {
          this.errorMessage.style.display = 'block';
          setTimeout(() => this.clearMessages(), 3000);
        }
      }
      
      showSuccess() {
        if (this.successMessage) {
          this.successMessage.style.display = 'block';
          setTimeout(() => this.clearMessages(), 3000);
        }
      }
      
      clearMessages() {
        if (this.errorMessage) this.errorMessage.style.display = 'none';
        if (this.successMessage) this.successMessage.style.display = 'none';
      }
      
      clearInput() {
        if (this.input) {
          this.input.value = '';
        }
      }
    }
  );
} 